import { isEqual, pick } from "lodash";

import { CartItem, Modifier } from "../../redux_store/cart/models";
import { FavouriteCartItem } from "../../redux_store/favourite/models";

const propertiesToCompare = ["productId", "isMiam"];

/**
 * Compares array of modifiers and returns true if arrays are equal.
 * @param cartItemModi
 * @param favouriteModi
 * @returns
 */
export const compareModis = (
  cartItemModi: Modifier[],
  favouriteModi: Modifier[]
): boolean => {
  const attributeToCheck = ["id"];

  if (cartItemModi.length === favouriteModi.length) {
    const compared = [];

    cartItemModi.forEach((modi) => {
      favouriteModi.forEach((favModi) => {
        if (
          isEqual(pick(modi, attributeToCheck), pick(favModi, attributeToCheck))
        ) {
          compared.push(true);
        }
      });
    });

    if (
      compared.length === cartItemModi.length &&
      compared.length === favouriteModi.length
    ) {
      return true;
    } else {
      return false;
    }
  } else {
    return false;
  }
};

/**
 * Checks if two arrays of modifiers are the same.
 * @param cartItemRemoveModi
 * @param favouriteItemRemoveModi
 * @returns
 */
export const compareModifiers = (
  cartItemRemoveModi?: Modifier[],
  favouriteItemRemoveModi?: Modifier[]
): boolean => {
  if (!!cartItemRemoveModi?.length && !!favouriteItemRemoveModi?.length) {
    return compareModis(cartItemRemoveModi, favouriteItemRemoveModi);
  } else if (cartItemRemoveModi?.length || favouriteItemRemoveModi?.length) {
    // if first condition failed, it means that modi arrays cannot be equal
    return false;
  }
  return true;
};

/**
 * Checks if all existing modifiers in cart item and in favourite item are equal.
 * @param cartItem
 * @param favouriteCartItem
 * @returns true if modifiers are equal
 */
const compareAllModifiers = (
  cartItem: CartItem,
  favouriteCartItem: CartItem
) => {
  const removeModifierCheck = compareModifiers(
    cartItem.removeModifier,
    favouriteCartItem.removeModifier
  );

  const addModifierCheck = compareModifiers(
    cartItem.addModifier,
    favouriteCartItem.addModifier
  );

  const extraModifierCheck = compareModifiers(
    cartItem.extraModifier,
    favouriteCartItem.extraModifier
  );

  const addFillingCheck = compareModifiers(
    cartItem.addFilling,
    favouriteCartItem.addFilling
  );

  // returns array with failed checks
  const modiEqualityCheck = [
    removeModifierCheck,
    addModifierCheck,
    extraModifierCheck,
    addFillingCheck,
  ].filter((check) => !check);
  // if at least one check failed, this line will return false
  return !modiEqualityCheck.length;
};

export const compareCartItemWithFavourite = (
  cartItem: CartItem,
  favouriteCartItem: FavouriteCartItem,
  excludeMiamCheck?: boolean
): boolean => {
  const comparingProperties = !excludeMiamCheck
    ? propertiesToCompare
    : propertiesToCompare.filter((item) => item != "isMiam");
  if (
    isEqual(
      pick(cartItem, comparingProperties),
      pick(favouriteCartItem.cartItem, comparingProperties)
    ) &&
    !cartItem.parts &&
    !favouriteCartItem.cartItem.parts
  ) {
    // if selected attributes of objects are equal, compare modifiers
    return compareAllModifiers(cartItem, favouriteCartItem.cartItem);
  }
  if (
    isEqual(
      pick(cartItem, comparingProperties),
      pick(favouriteCartItem.cartItem, comparingProperties)
    ) &&
    cartItem.parts &&
    favouriteCartItem.cartItem.parts
  ) {
    // compare parts
    const compared = [];
    favouriteCartItem.cartItem.parts.forEach((part) => {
      cartItem.parts.forEach((cartItemPart) => {
        if (
          isEqual(
            pick(part, comparingProperties),
            pick(cartItemPart, comparingProperties)
          )
        ) {
          // if selected attributes of objects are equal, compare modifiers
          return compareAllModifiers(part, cartItemPart) && compared.push(true);
        }
      });
    });
    if (
      compared.length === favouriteCartItem.cartItem.parts.length &&
      compared.length === cartItem.parts.length
    ) {
      return true;
    }
  }
  return false;
};

export const isFavouriteItem = (
  cartItem: CartItem,
  favouriteCartItems: FavouriteCartItem[],
  excludeMiamCheck?: boolean
): boolean => {
  let isFavouriteItem = false;
  for (const favouriteCartItem of favouriteCartItems) {
    if (
      compareCartItemWithFavourite(
        cartItem,
        favouriteCartItem,
        excludeMiamCheck
      )
    ) {
      isFavouriteItem = true;
      break;
    }
  }

  return isFavouriteItem;
};
